import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { Company } from '@/types/company';
import { fetchCompanies, getDataMode } from '@/lib/data-service';
import { useUserPreferencesSync } from '@/hooks/use-user-preferences-sync';
import { setAuthCompanyId } from '@/lib/auth-fetch';
import { useDataMode } from '@/contexts/DataModeContext';

interface CompanyContextType {
  currentCompany: Company | null;
  availableCompanies: Company[];
  isLoading: boolean;
  error: string | null;
  switchCompany: (companyId: string) => Promise<void>;
  refreshCompanies: () => Promise<void>;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

const STORAGE_KEY = 'ebprojects_current_company';

interface CompanyProviderProps {
  children: ReactNode;
}

export function CompanyProvider({ children }: CompanyProviderProps) {
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [availableCompanies, setAvailableCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { loadPreferences, saveSelectedCompany } = useUserPreferencesSync();
  const { mode } = useDataMode();

  const loadCompanies = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // fetchCompanies handles demo/live mode switching internally
      const activeCompanies = await fetchCompanies() as Company[];

      setAvailableCompanies(activeCompanies);

      // Try to restore from backend first (in live mode), then fallback to local storage
      let companyToLoad: Company | null = null;
      const dataMode = getDataMode();
      
      if (dataMode === 'live') {
        // Try to load from backend preferences
        const backendPrefs = await loadPreferences();
        if (backendPrefs?.selectedCompanyId) {
          companyToLoad = activeCompanies.find((c) => c.id === backendPrefs.selectedCompanyId) || null;
        }
      }
      
      // Fallback to local storage if backend didn't have a selection
      if (!companyToLoad) {
        const storedCompanyId = localStorage.getItem(STORAGE_KEY);
        if (storedCompanyId) {
          companyToLoad = activeCompanies.find((c) => String(c.id) === storedCompanyId) || null;
        }
      }

      // If no stored company or stored company not found, use first available
      if (!companyToLoad && activeCompanies.length > 0) {
        companyToLoad = activeCompanies[0];
      }

      if (companyToLoad) {
        setAuthCompanyId(companyToLoad.instanceId ?? undefined);
        setCurrentCompany(companyToLoad);
        localStorage.setItem(STORAGE_KEY, String(companyToLoad.id));
      } else {
        setError('No companies available');
      }
    } catch (err) {
      console.error('Failed to load companies:', err);
      setError('Failed to load companies');
    } finally {
      setIsLoading(false);
    }
  }, [loadPreferences]);

  // Load companies on mount and when data mode changes
  useEffect(() => {
    loadCompanies();
  }, [loadCompanies, mode]);

  useEffect(() => {
    setAuthCompanyId(currentCompany?.instanceId ?? undefined);
  }, [currentCompany]);

  const switchCompany = useCallback(
    async (companyId: string) => {
      try {
        setIsLoading(true);
        setError(null);

        const company = availableCompanies.find((c) => String(c.id) === companyId);
        if (!company) {
          throw new Error('Company not found or access denied');
        }

        setAuthCompanyId(company.instanceId ?? undefined);

        setCurrentCompany(company);
        localStorage.setItem(STORAGE_KEY, companyId);

        const dataMode = getDataMode();
        if (dataMode === 'live') {
          saveSelectedCompany(company.id).catch((err) => {
            console.error('Failed to save selected company to backend:', err);
          });
        }

        window.dispatchEvent(new CustomEvent('company-changed', { detail: { companyId } }));
      } catch (err) {
        console.error('Failed to switch company:', err);
        setError('Failed to switch company');
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [availableCompanies, saveSelectedCompany]
  );

  const refreshCompanies = useCallback(async () => {
    await loadCompanies();
  }, [loadCompanies]);

  const value: CompanyContextType = {
    currentCompany,
    availableCompanies,
    isLoading,
    error,
    switchCompany,
    refreshCompanies,
  };

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

export function useCompany(): CompanyContextType {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
}
